/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { useForm } from "@/src/provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import { dateFieldDefault, isRequired, pastOrSameDateDefault, requiredValidator } from "@/src/services/validationRules";
import Group from "@/src/components/Wizard/Group";
import OnceIcon from "@/src/assets/icons/traffic/toggleable/IconLicencePointsOne";
import MultipleIcon from "@/src/assets/icons/contract/toggleable/IconRepeat";
import { translate } from "@/src/services/translations/translations";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import Address from "@/src/components/Form/Common/Address/Address";
import TextFieldConfigured from "@/src/components/Form/ConfiguredFormElements/TextFieldConfigured/TextFieldConfigured";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";

type AdditionalProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function Incident({ submitProps, product }: AdditionalProps) {
  const { values, handleSubmit, handleChange } = useForm();

  useEffect(() => {
    if (
      values.incidentLocalityDescription === null &&
      values.incidentAddress.streetAddress === null &&
      values.incidentAddress.postalCode === null &&
      values.incidentAddress.addressLocality === null
    ) {
      handleChange({
        target: {
          name: "hasPreciseIncidentAddress",
          value: null,
          type: "text",
        },
      });
    } else {
      handleChange({
        target: {
          name: "hasPreciseIncidentAddress",
          value: values.incidentLocalityDescription !== null ? "no" : "yes",
          type: "text",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (values.hasPreciseIncidentAddress === "no") {
      handleChange({
        target: {
          name: "incidentAddress.streetAddress",
          value: null,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "incidentAddress.postalCode",
          value: null,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "incidentAddress.addressLocality",
          value: null,
          type: "text",
        },
      });
    } else if (values.hasPreciseIncidentAddress === "yes") {
      handleChange({
        target: {
          name: "incidentLocalityDescription",
          value: null,
          type: "text",
        },
      });
    }
  }, [values.hasPreciseIncidentAddress]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Anliegen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Informationen zum Anliegen</strong>
              </FormLegend>
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{translate(`${product.name}.incidentDate.question`, values)}</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                name="incidentDate"
                label={translate(`${product.name}.incidentDate.label`, values)}
                validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault, pastOrSameDateDefault]}
              />
            </Grid>
            {values.objectivesClient.indexOf("injunction") > -1 && (
              <Grid size={12}>
                <Group
                  type="radio"
                  name="incidentFrequency"
                  question={translate(`${product.name}.incidentFrequency.question`, values)}
                  options={[
                    {
                      labelText: translate(`${product.name}.incidentFrequency.options.once.label`, values),
                      labelIcon: <OnceIcon />,
                      labelIconActive: <OnceIcon active />,
                      value: "once",
                    },
                    {
                      labelText: translate(`${product.name}.incidentFrequency.options.multiple.label`, values),
                      labelIcon: <MultipleIcon />,
                      labelIconActive: <MultipleIcon active />,
                      value: "multiple",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}
            <Grid size={12}>
              <Group
                type="radio"
                name="hasPreciseIncidentAddress"
                question={translate(`${product.name}.hasPreciseIncidentAddress.question`, values)}
                options={[
                  {
                    labelText: translate(`${product.name}.hasPreciseIncidentAddress.options.yes.label`, values),
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: translate(`${product.name}.hasPreciseIncidentAddress.options.no.label`, values),
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                  },
                ]}
              />
            </Grid>
            {values.hasPreciseIncidentAddress === "yes" && <Address path="incidentAddress" headline={" Bitte hinterlegen Sie die Adresse"} />}
            {values.hasPreciseIncidentAddress === "no" && <TextFieldConfigured caseData={values} fieldPath={"incidentLocalityDescription"} multiline />}
            <Grid size={12}>
              <Group
                type="radio"
                name="policeInvolved"
                question={translate(`${product.name}.policeInvolved.question`, values)}
                options={[
                  {
                    labelText: translate(`${product.name}.policeInvolved.options.yes.label`, values),
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: translate(`${product.name}.policeInvolved.options.no.label`, values),
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                  },
                ]}
              />
            </Grid>
          </Grid>
          {values.policeInvolved === "yes" && (
            <>
              <Grid
                size={12}
                sx={[
                  {
                    mb: 4,
                  },
                  wizardStyle.gridTopSpaced,
                ]}
              >
                <FormLegend>
                  Damit wir Akteneinsicht für Sie beantragen können, benötigen wir folgende Angaben. Bitte tragen Sie diese ein, sofern sie Ihnen vorliegen.
                </FormLegend>
              </Grid>
              <Grid size={12}>
                <ValidatorTextField name="policeReportReferenceNumber" label={translate(`${product.name}.policeReportReferenceNumber.label`, values)} />
              </Grid>
              <Grid size={12}>
                <ValidatorTextField name="policeDepartmentName" label={translate(`${product.name}.policeDepartmentName.label`, values)} />
              </Grid>
            </>
          )}
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
